
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { ExternalLink, Info } from "lucide-react";
import { Investor, Contact } from "@/types/investor";
import { hookDebugUtils } from "@/utils/ai/debugUtils";

interface InvestorInfoCardProps {
  investor: Investor;
  selectedContactData: Contact | null;
  isCustomContact: boolean;
}

// Safe rendering helper function to prevent React error #31
const safeRender = (value: any): React.ReactNode => {
  if (value === null || value === undefined) return "Not available";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((item, index) => (
      <span key={index}>
        {safeRender(item)}
        {index < value.length - 1 && ", "}
      </span>
    ));
  }
  // For objects, stringify them to prevent React error #31
  return hookDebugUtils.safeStringify(value, "Object data");
};

const InvestorInfoCard = ({ 
  investor, 
  selectedContactData,
  isCustomContact 
}: InvestorInfoCardProps) => {
  // Verify we're not rendering invalid data types
  const portfolioCompanies = investor?.portfolioCompanies || [];
  const keywords = investor?.keywords || [];
  const notes = investor?.notes || "";
  const sources = investor?.sources || [];

  return (
    <Card className="bg-gray-50">
      <CardContent className="pt-6 space-y-2">
        <div>
          <p className="text-sm font-medium">Firm:</p>
          <div className="flex items-center">
            <p>{safeRender(investor.firm)}</p>
            {investor.website && (
              <a 
                href={investor.website} 
                target="_blank" 
                rel="noopener noreferrer"
                className="ml-2 text-blue-600 hover:text-blue-800 inline-flex items-center"
              >
                <ExternalLink size={14} className="ml-1" />
                <span className="sr-only">Visit website</span>
              </a>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div>
            <p className="text-sm font-medium">Relevance Score:</p>
            <p>{typeof investor.relevanceScore === 'number' ? investor.relevanceScore.toFixed(1) : '0.0'}/10</p>
          </div>
          {investor.confidence && (
            <div>
              <p className="text-sm font-medium">Data Confidence:</p>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Badge 
                      variant={
                        investor.confidence === 'high' ? "success" : 
                        investor.confidence === 'medium' ? "warning" : "destructive"
                      }
                    >
                      {investor.confidence}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-sm">
                      {investor.confidence === 'high' 
                        ? 'Information verified from multiple reliable sources' 
                        : investor.confidence === 'medium'
                        ? 'Information found but not fully verified'
                        : 'Limited verifiable information found'}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
        </div>
        <div>
          <p className="text-sm font-medium">Investment Focus:</p>
          <p>{safeRender(investor.investmentFocus) || "Not available"}</p>
        </div>
        {portfolioCompanies.length > 0 && (
          <div>
            <p className="text-sm font-medium">Portfolio Companies:</p>
            <div className="flex flex-wrap gap-1">
              {portfolioCompanies.map((company, idx) => {
                if (typeof company !== 'string') {
                  return <span key={idx}>{safeRender(company)}</span>;
                }
                
                // Check if the company has a URL format (added during enrichment)
                const parts = company.split('|');
                const companyName = parts[0].trim();
                const companyUrl = parts.length > 1 ? parts[1].trim() : null;
                
                return companyUrl ? (
                  <a 
                    key={idx}
                    href={companyUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-blue-600 hover:text-blue-800 inline-flex items-center"
                  >
                    {companyName}
                    <ExternalLink size={12} className="ml-1" />
                    {idx < portfolioCompanies.length - 1 && ", "}
                  </a>
                ) : (
                  <span key={idx}>
                    {companyName}
                    {idx < portfolioCompanies.length - 1 && ", "}
                  </span>
                );
              })}
            </div>
          </div>
        )}
        {keywords.length > 0 && (
          <div>
            <p className="text-sm font-medium">Keywords:</p>
            <p>{typeof keywords.join === 'function' ? keywords.join(", ") : safeRender(keywords)}</p>
          </div>
        )}
        {notes && (
          <div>
            <p className="text-sm font-medium">Notes:</p>
            <p className="text-xs text-gray-600">{safeRender(notes)}</p>
          </div>
        )}
        {/* Display sources */}
        {sources.length > 0 && (
          <div>
            <p className="text-sm font-medium flex items-center gap-1">
              Sources: <Info size={14} className="text-gray-500" />
            </p>
            <div className="max-h-24 overflow-y-auto text-xs space-y-1">
              {sources.map((source, index) => (
                <div key={index} className="flex items-start">
                  <span className="text-gray-500 mr-1">{index + 1}.</span>
                  {typeof source === 'string' ? (
                    <a 
                      href={source} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-blue-600 hover:text-blue-800 break-all inline-flex items-center"
                    >
                      {source.length > 60 ? source.substring(0, 60) + '...' : source}
                      <ExternalLink size={10} className="ml-1 inline-block" />
                    </a>
                  ) : (
                    <span>{safeRender(source)}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {selectedContactData && (
          <div>
            <p className="text-sm font-medium">Selected Contact:</p>
            <p>
              {safeRender(selectedContactData.name)} 
              {isCustomContact 
                ? ` - ${safeRender(selectedContactData.email)}`
                : ` - ${safeRender(selectedContactData.role || "No role")}`
              }
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InvestorInfoCard;
